import { httpsCallable } from 'firebase/functions';
import { useState } from 'react';
import { functions } from '../../lib/firebase';

/**
 * deleteAssignmentSet callable hook。
 *
 * 配分セットの削除は firestore.rules で `allow delete: if false` のため、クライアント `deleteDoc` では
 * 常に失敗する。Admin SDK の callable が `assignments` サブコレクションごと削除する
 * (回答 `posts/{id}/verifications` には触らない)。
 *
 * `useGenerateAssignments` と同 pattern (idle / pending / success / error + call / reset)。
 */

export interface DeleteAssignmentSetInput {
  districtId: string;
  setId: string;
}

export interface DeleteAssignmentSetResult {
  /** 削除した assignments (uid × post) の件数。 */
  deletedAssignments: number;
}

export type UseDeleteAssignmentSetState =
  | { status: 'idle'; result: null; error: null }
  | { status: 'pending'; result: null; error: null }
  | { status: 'success'; result: DeleteAssignmentSetResult; error: null }
  | { status: 'error'; result: null; error: Error };

export interface UseDeleteAssignmentSetReturn {
  state: UseDeleteAssignmentSetState;
  call: (input: DeleteAssignmentSetInput) => Promise<DeleteAssignmentSetResult | null>;
  reset: () => void;
}

export function useDeleteAssignmentSet(): UseDeleteAssignmentSetReturn {
  const [state, setState] = useState<UseDeleteAssignmentSetState>({
    status: 'idle',
    result: null,
    error: null,
  });

  async function call(input: DeleteAssignmentSetInput): Promise<DeleteAssignmentSetResult | null> {
    setState({ status: 'pending', result: null, error: null });
    try {
      const fn = httpsCallable<DeleteAssignmentSetInput, DeleteAssignmentSetResult>(
        functions,
        'deleteAssignmentSet',
      );
      const res = await fn(input);
      setState({ status: 'success', result: res.data, error: null });
      return res.data;
    } catch (e) {
      const err = e as Error;
      setState({ status: 'error', result: null, error: err });
      return null;
    }
  }

  function reset() {
    setState({ status: 'idle', result: null, error: null });
  }

  return { state, call, reset };
}
